import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  CheckCircle2, Clock, AlertTriangle,
  Eye, Search, Plus, Building2, Mail,
  RefreshCw, Loader2, ThumbsUp, ThumbsDown, CircleDashed,
} from 'lucide-react';
import { onboardingAPI, suppliersAPI, intelligenceAPI, fetchAllPages, SupplierInvitation, OnboardingWorkflow, FilledDataset, OnboardingDataset, QualificationTemplate, PendingInitialSupplier, PendingSupplierDetail, SupplierProfile, SupplierQualification } from '../../lib/api';
import { toast } from 'sonner';
import { useIsSupplier } from '../../hooks/usePortalMode';
import { useCanApproveSuppliers } from '../../hooks/useCanApproveSuppliers';
import { useView } from '../../contexts/ViewContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatDistanceToNow, format } from 'date-fns';
import { statusColors } from './onboardingConstants';
import { SupplierOnboardingView } from './SupplierOnboardingView';
import DraftSuppliersTable from './DraftSuppliersTable';
import { InvitationsTab, PendingReviewsTab } from './CustomerOnboardingTabs';
import { groupPendingDueDiligenceSuppliers, deriveOrphanWorkflows, PDD_ORPHAN_WORKFLOW_STATUSES } from './onboardingSelectors';
import {
  WIP_WORKFLOW_STATUSES,
  selectInvitedStageInvitations,
  computeOnboardingStageCounts,
} from './onboardingStageCounts';

interface FilledDatasetWithDetails extends FilledDataset {
  supplier_name?: string;
  dataset_name?: string;
}

// CR-4817: Pending Approvals assign list merges two sources that track
// questionnaires separately in the backend — OnboardingDataset (used by the
// onboarding pipeline) and QualificationTemplate (authored on the Questionnaires
// page). The dropdown treats them uniformly; the approve handler splits by
// source and sends each list to the right backend field.
type AssignableTemplateSource = 'dataset' | 'qualification';
interface AssignableTemplate {
  key: string;            // compound key (source + id) for React + selection state
  id: number;             // numeric id sent back to the backend
  source: AssignableTemplateSource;
  name: string;
  type: string;           // dataset_type or template_type — for display only
  version: string;
  questions_count: number;
}

export default function OnboardingDashboard() {
  const { formatCurrency } = useFormatters();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSupplier = useIsSupplier();
  // CR-6507: Supplier Onboarding must respect the current Group/Division/Site
  // view like the rest of the platform. viewKey changes whenever the top-bar
  // view switches, so the fetch effect re-runs; getViewQueryParams() forwards
  // the ?group/?company/?site scope to the list endpoints.
  const { viewKey, getViewQueryParams, isLoading: viewContextLoading } = useView();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('drafts');
  const [loading, setLoading] = useState(true);
  // CR-5965: the statistic tiles no longer read GET /onboarding/dashboard/ —
  // they are derived from the tab records instead (see onboardingStageCounts),
  // so nothing on this page consumes that payload any more and the request has
  // been dropped from fetchData. The endpoint itself is unchanged for any other
  // caller.
  const [invitations, setInvitations] = useState<SupplierInvitation[]>([]);
  const [workflows, setWorkflows] = useState<OnboardingWorkflow[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [myFilledDatasets, setMyFilledDatasets] = useState<FilledDataset[]>([]);
  const [myWorkflow, setMyWorkflow] = useState<OnboardingWorkflow | null>(null);
  const [pendingReviews, setPendingReviews] = useState<FilledDatasetWithDetails[]>([]);
  // CR-5984 Path B: legacy questionnaires live in SupplierQualification rather
  // than FilledDataset. Fetched separately so we can surface them in PDD
  // alongside the FilledDataset-driven reviews and keep "Awaiting Questionnaire
  // Assignment" honest (we only badge as orphan when both sources are empty).
  const [submittedQualifications, setSubmittedQualifications] = useState<SupplierQualification[]>([]);
  // A questionnaire that has been approved leaves every "outstanding" bucket,
  // so the card that reviewed it had nothing left to show and the only link on
  // it was "View Full Profile" — the initial-profile wizard, which never
  // contains a single questionnaire answer. Approved rows are fetched
  // SEPARATELY rather than folded into submittedQualifications: those drive the
  // outstanding counts and the Ready-to-Approve state, and must keep counting
  // only what is still to do.
  const [settledQualifications, setSettledQualifications] = useState<SupplierQualification[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<SupplierInvitation[]>([]);
  const [pendingInitialSuppliers, setPendingInitialSuppliers] = useState<PendingInitialSupplier[]>([]);
  // Drafts = pre-wizard-submission supplier profiles (SupplierProfile.onboarding_status='draft').
  const [draftSuppliers, setDraftSuppliers] = useState<SupplierProfile[]>([]);
  // Approved suppliers (SupplierProfile.is_approved=true) for the Approved tab.
  const [approvedSuppliers, setApprovedSuppliers] = useState<SupplierProfile[]>([]);
  /** Profiles the backend marks as past invitation, used only to exit the Invited stage. */
  const [dueDiligenceSuppliers, setDueDiligenceSuppliers] = useState<SupplierProfile[]>([]);
  // Page indexes for client-side pagination (per tab, 1-based).
  const [draftPage, setDraftPage] = useState(1);
  const [approvedPage, setApprovedPage] = useState(1);
  const TAB_PAGE_SIZE = 25;
  const [availableTemplates, setAvailableTemplates] = useState<AssignableTemplate[]>([]);
  // Selection is stored by compound key so the two ID namespaces don't collide.
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  // CR-4141: gates the supplier approve/reject/send-invitation actions and
  // the questionnaire-assignment picker (which only takes effect on approval).
  const canApproveSuppliers = useCanApproveSuppliers(!isSupplier);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewSupplierId, setReviewSupplierId] = useState<number | null>(null);
  // CR-5894: row id (e.g. "wf-12") to highlight in the Declined tab when the
  // "onboarding rejected" notification deep-links here.
  const [highlightedDeclinedId, setHighlightedDeclinedId] = useState<string | null>(null);
  const highlightedDeclinedRowRef = useRef<HTMLTableRowElement | null>(null);
  const [supplierDetail, setSupplierDetail] = useState<PendingSupplierDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | string | null>(null);
  /** Invitation currently being advanced to due diligence, for row-level progress. */
  const [advancingInvitationId, setAdvancingInvitationId] = useState<number | null>(null);
  // datasets state removed - no longer needed after tab restructure
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectTarget, setRejectTarget] = useState<{ type: 'invitation' | 'supplier'; id: number } | null>(null);

  useEffect(() => {
    // CR-6507: wait until the org view has resolved, then (re)fetch whenever it
    // changes so the tabs reflect the selected Group/Division/Site.
    if (viewContextLoading) return;
    if (isSupplier) {
      fetchSupplierData();
    } else {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSupplier, viewKey, viewContextLoading]);

  // Auto-open review modal when navigated with ?review=<profileId>
  useEffect(() => {
    const reviewId = searchParams.get('review');
    if (reviewId && !loading) {
      const profileId = Number(reviewId);
      if (profileId > 0) {
        handleOpenReviewModal(profileId);
        // Clear the query param so refreshing doesn't re-open
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, loading]);

  // CR-4758: Honour ?tab=<value> so the wizard can deep-link to a specific
  // tab (e.g. Pending Approvals after a new supplier is submitted).
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
      // Drop the query param so a refresh doesn't bounce the user away from
      // wherever they navigate next.
      const next = new URLSearchParams(searchParams);
      next.delete('tab');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // CR-5894: deep link from the "onboarding rejected" notification —
  // ?declined=<rowId> lands on the Declined tab and highlights the rejected
  // supplier's record. Wait for data so the row exists before we highlight it.
  useEffect(() => {
    const declinedId = searchParams.get('declined');
    if (declinedId && !loading) {
      setActiveTab('declined');
      setHighlightedDeclinedId(declinedId);
      const next = new URLSearchParams(searchParams);
      next.delete('declined');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, loading, setSearchParams]);

  // Scroll the highlighted declined row into view once it has rendered.
  useEffect(() => {
    if (highlightedDeclinedId && highlightedDeclinedRowRef.current) {
      highlightedDeclinedRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightedDeclinedId, workflows, invitations]);

  const fetchSupplierData = async () => {
    setLoading(true);
    try {
      const [filledRes, workflowsRes] = await Promise.all([
        onboardingAPI.getFilledDatasets(),
        onboardingAPI.getWorkflows(),
      ]);
      const filledData = filledRes.data.results || filledRes.data || [];
      setMyFilledDatasets(filledData);
      const wfData = workflowsRes.data.results || workflowsRes.data || [];
      if (wfData.length > 0) setMyWorkflow(wfData[0]);
    } catch (error) {
      console.error('Failed to fetch supplier onboarding data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // CR-4799: Pending Due Diligence used to only surface status='submitted',
      // so once a customer rejected a questionnaire or asked for more
      // information the record disappeared from the tab with no other place
      // to track it. Fetch every status the customer can still act on — the
      // tab now includes rejected / more_info_required / under_review rows
      // alongside freshly submitted ones, and each row's badge surfaces the
      // specific state.
      const PENDING_DUE_DILIGENCE_STATUSES = ['submitted', 'under_review', 'more_info_required', 'rejected'];
      // CR-6507: forward the current view scope (?group/?company/?site) to every
      // record-list endpoint so switching Group/Division/Site re-scopes the tabs.
      // The pending-approval endpoints take no params arg and fall back to the
      // same view persisted server-side on the user profile.
      const viewParams = getViewQueryParams();
      // Multi-page fetches below use fetchAllPages — the backend clamps
      // page_size to 100, so the old single big-page requests silently
      // capped each status bucket / list at one page.
      const [invitationsRes, workflowsRes, filledDatasetsArr, qualificationsArr, pendingApprovalsRes, pendingInitialRes, allDatasets, allQualTemplates, draftSuppliersRes, approvedSuppliersRes, dueDiligenceSuppliersRes] = await Promise.all([
        onboardingAPI.getInvitations(viewParams),
        onboardingAPI.getWorkflows(viewParams),
        Promise.all(PENDING_DUE_DILIGENCE_STATUSES.map(s =>
          fetchAllPages<FilledDataset>((page) =>
            onboardingAPI.getFilledDatasets({ status: s, page_size: 100, page, ...viewParams }))
        )),
        // CR-5984 Path B: also fetch SupplierQualification rows in the same
        // statuses so PDD reflects them. On tenants where questionnaires only
        // live in the legacy SupplierQualification model this is the only way
        // the supplier appears with their actual submitted responses.
        Promise.all(PENDING_DUE_DILIGENCE_STATUSES.map(s =>
          fetchAllPages<SupplierQualification>((page) =>
            intelligenceAPI.getQualifications({ status: s, page_size: 100, page, ...viewParams }))
        )),
        onboardingAPI.getPendingApprovals(),
        onboardingAPI.getPendingInitialSuppliers(),
        // CR-4817: assign-list now unions both questionnaire sources — the
        // onboarding pipeline's OnboardingDataset AND the Questionnaires page's
        // QualificationTemplate — because customers create on the latter but
        // the previous behaviour only listed the former, hiding freshly
        // authored questionnaires (e.g. "Test Assessment") from the list.
        fetchAllPages<OnboardingDataset>((page) =>
          onboardingAPI.getDatasets({ is_active: true, page_size: 100, page })),
        fetchAllPages<QualificationTemplate>((page) =>
          intelligenceAPI.getTemplates({ status: 'active', page_size: 100, page })),
        // Drafts = wizard-in-progress profiles; supplier hasn't submitted for review yet.
        suppliersAPI.getProfiles({ minimal: true, onboarding_status: 'draft', page_size: 1000, ...viewParams }),
        // Approved = fully onboarded suppliers.
        suppliersAPI.getProfiles({ minimal: true, is_approved: true, page_size: 1000, ...viewParams }),
        // Suppliers the BACKEND already considers past invitation. The Invited
        // rule used to infer this purely from submitted questionnaires, so a
        // supplier whose profile said 'due_diligence' but who had no
        // FilledDataset rows yet stayed pinned in Invited for good: PDD's
        // orphan-workflow path is the one that should claim them, and it
        // excludes anyone still in the Invited stage (onboardingSelectors.ts).
        suppliersAPI.getProfiles({ minimal: true, onboarding_status: 'due_diligence', page_size: 1000, ...viewParams }),
      ]);
      setInvitations(invitationsRes.data.results || invitationsRes.data || []);
      const allWorkflows = workflowsRes.data.results || workflowsRes.data || [];
      setWorkflows(allWorkflows);
      setDraftSuppliers(draftSuppliersRes.data.results || []);
      setApprovedSuppliers(approvedSuppliersRes.data.results || []);
      setDueDiligenceSuppliers(dueDiligenceSuppliersRes.data.results || []);
      // CR-4799: flatten multi-status responses into one list; dedupe by id
      // in case the backend ever widens a state machine and a row transitions
      // between fetches (defensive — unlikely in practice given Promise.all).
      const combinedFilled = filledDatasetsArr.flat();
      const seenIds = new Set<number>();
      const uniqueFilled = combinedFilled.filter(d => {
        if (seenIds.has(d.id)) return false;
        seenIds.add(d.id);
        return true;
      });
      setPendingReviews(uniqueFilled);

      // CR-5984 Path B: flatten + dedupe SupplierQualification rows, then
      // scope to suppliers that appear in the workflows the customer can see.
      // Without that scope filter, customers would see every SQ system-wide
      // for suppliers they aren't actively onboarding.
      const combinedQuals = qualificationsArr.flat();
      const seenQualIds = new Set<number>();
      const uniqueQuals = combinedQuals.filter(q => {
        if (seenQualIds.has(q.id)) return false;
        seenQualIds.add(q.id);
        return true;
      });
      const onboardingSupplierIds = new Set<number>(allWorkflows.map(w => w.supplier).filter((s): s is number => s != null));
      const scopedQuals = uniqueQuals.filter(q => onboardingSupplierIds.has(q.supplier));
      setSubmittedQualifications(scopedQuals);
      // Approved questionnaires, read-only, fetched PER SUPPLIER rather than
      // as one `status=approved` sweep. The sweep walks up to 50 sequential
      // pages of every approval the tenant has ever recorded, on every load of
      // this page, to display a handful of rows. Suppliers mid-onboarding are
      // inherently a small working set, so N small parallel requests is both
      // bounded and cheaper.
      const dueDiligenceSupplierIds = Array.from(new Set(
        allWorkflows
          .filter((w: OnboardingWorkflow) => w.status !== 'declined' && w.status !== 'suspended')
          .map((w: OnboardingWorkflow) => w.supplier)
          .filter((sid): sid is number => sid != null)
      ));
      const settledPerSupplier = await Promise.all(
        dueDiligenceSupplierIds.map(sid =>
          intelligenceAPI
            .getQualifications({ status: 'approved', supplier: sid, page_size: 100, ...viewParams })
            .then(r => r.data.results || [])
            // One supplier's approvals failing must not blank the whole tab.
            .catch(() => [] as SupplierQualification[])
        )
      );
      setSettledQualifications(settledPerSupplier.flat());

      setPendingApprovals(pendingApprovalsRes.data.results || pendingApprovalsRes.data || []);
      setPendingInitialSuppliers(pendingInitialRes.data.results || []);

      // CR-4817 + CR-4827: merge OnboardingDataset and QualificationTemplate
      // sources into one dropdown, then dedupe to one row per name.
      // Version strings like "Issue 1/2/3" and "Latest" are authored per-template;
      // prefer "Latest" when present, otherwise fall back to the lexicographically
      // greatest version string (stable for the Issue N pattern). Without this
      // the dropdown showed Data Protection / ESG / Governance four times each
      // (one per issue), which is what CR-4827 is about.
      const datasets = allDatasets;
      const qtemplates = allQualTemplates;
      const merged: AssignableTemplate[] = [
        ...datasets.map((d): AssignableTemplate => ({
          key: `d:${d.id}`,
          id: d.id,
          source: 'dataset',
          name: d.name,
          type: d.dataset_type || '',
          version: d.version || '',
          questions_count: d.questions_count || 0,
        })),
        ...qtemplates.map((t): AssignableTemplate => ({
          key: `q:${t.id}`,
          id: t.id,
          source: 'qualification',
          name: t.name,
          type: t.template_type || '',
          version: String(t.version || ''),
          questions_count: t.question_count || 0,
        })),
      ];
      const versionRank = (v: string): number => {
        const s = (v || '').trim().toLowerCase();
        if (s === 'latest') return Number.MAX_SAFE_INTEGER;
        const m = s.match(/(\d+(?:\.\d+)?)/);
        return m ? parseFloat(m[1]) : -1;
      };
      const byName = new Map<string, AssignableTemplate>();
      for (const t of merged) {
        const k = t.name.trim().toLowerCase();
        const prev = byName.get(k);
        if (!prev || versionRank(t.version) > versionRank(prev.version)) {
          byName.set(k, t);
        }
      }
      setAvailableTemplates(Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Failed to fetch onboarding data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvitation = async (id: number) => {
    try { await onboardingAPI.sendInvitation(id); fetchData(); }
    catch (error) { console.error('Failed to send invitation:', error); }
  };

  const handleResendInvitation = async (id: number) => {
    try { await onboardingAPI.resendInvitation(id); fetchData(); }
    catch (error) { console.error('Failed to resend invitation:', error); }
  };

  /**
   * Move an invited supplier on to due diligence by hand. For invitations the
   * supplier can never accept — the email didn't arrive — where resending is
   * pointless and the supplier would otherwise sit in Invited indefinitely.
   */
  const handleAdvanceToDueDiligence = async (id: number) => {
    setAdvancingInvitationId(id);
    try {
      const res = await onboardingAPI.advanceToDueDiligence(id);
      const created = res.data.questionnaires_created;
      toast.success(
        created > 0
          ? `Moved to Due Diligence — ${created} questionnaire${created === 1 ? '' : 's'} ready to review.`
          : 'Moved to Due Diligence. No questionnaires are attached to this invitation yet — assign an onboarding dataset to start the review.'
      );
      fetchData();
    } catch (error: unknown) {
      console.error('Failed to advance invitation:', error);
      const detail = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(detail || 'Failed to move supplier to due diligence');
    } finally {
      setAdvancingInvitationId(null);
    }
  };

  const handleApproveToSend = async (id: number) => {
    setActionLoading(id);
    try { await onboardingAPI.approveToSend(id); fetchData(); }
    catch (error) { console.error('Failed to approve invitation:', error); }
    finally { setActionLoading(null); }
  };

  const handleRejectApproval = (id: number) => {
    setRejectTarget({ type: 'invitation', id });
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleOpenReviewModal = async (profileId: number) => {
    setReviewSupplierId(profileId);
    setReviewModalOpen(true);
    setSupplierDetail(null);
    setDetailLoading(true);
    try {
      const res = await onboardingAPI.getPendingSupplierDetail(profileId);
      setSupplierDetail(res.data);
    } catch (error) {
      console.error('Failed to load supplier details:', error);
      toast.error('Failed to load supplier details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseReviewModal = () => {
    setReviewModalOpen(false);
    setReviewSupplierId(null);
    setSupplierDetail(null);
  };

  const handleApprovePendingSupplier = async (profileId: number) => {
    setActionLoading(`supplier-${profileId}`);
    try {
      // CR-4817: split selected compound keys by source and send each list as
      // the matching backend field. Datasets → template_ids, qualification
      // templates → qualification_template_ids.
      const templateIds: number[] = [];
      const qualificationTemplateIds: number[] = [];
      for (const key of selectedTemplateIds) {
        const picked = availableTemplates.find(t => t.key === key);
        if (!picked) continue;
        (picked.source === 'qualification' ? qualificationTemplateIds : templateIds).push(picked.id);
      }
      const res = await onboardingAPI.approvePendingSupplier(profileId, {
        templateIds: templateIds.length > 0 ? templateIds : undefined,
        qualificationTemplateIds: qualificationTemplateIds.length > 0 ? qualificationTemplateIds : undefined,
      });
      toast.success(`Supplier approved. ${res.data.questionnaires_assigned} questionnaire(s) assigned.`);
      handleCloseReviewModal();
      fetchData();
    } catch (error) {
      console.error('Failed to approve supplier:', error);
      toast.error('Failed to approve supplier');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectPendingSupplier = (profileId: number) => {
    setRejectTarget({ type: 'supplier', id: profileId });
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleConfirmReject = async () => {
    if (!rejectTarget || !rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    const { type, id } = rejectTarget;
    if (type === 'invitation') {
      setActionLoading(id);
      try { await onboardingAPI.rejectApproval(id, rejectReason); fetchData(); }
      catch { toast.error('Failed to reject invitation'); }
      finally { setActionLoading(null); }
    } else {
      setActionLoading(`supplier-${id}`);
      try {
        await onboardingAPI.rejectPendingSupplier(id, rejectReason);
        toast.success('Supplier rejected');
        handleCloseReviewModal();
        fetchData();
      } catch {
        toast.error('Failed to reject supplier');
      } finally {
        setActionLoading(null);
      }
    }
    setRejectDialogOpen(false);
    setRejectTarget(null);
    setRejectReason('');
  };

  const needle = searchQuery.trim().toLowerCase();
  const matchesSearch = (...fields: Array<string | undefined | null>) =>
    !needle || fields.some((f) => typeof f === 'string' && f.toLowerCase().includes(needle));

  const filteredInvitations = invitations.filter(inv =>
    matchesSearch(inv.invitee_company_name, inv.invitee_email)
  );

  // CR-5953: keep suppliers visible in the Invited tab once they've
  // accepted the invitation + registered, until their questionnaires
  // are submitted. Previously they vanished from every tab between
  // account activation and questionnaire submission because the
  // Invited filter dropped 'accepted' invitations and the Pending Due
  // Diligence tab keys off FilledDataset.status='submitted' (or
  // later). Cross-reference the workflow row by supplier + customer to
  // tell whether the supplier has progressed past questionnaire
  // submission — if not, they belong in Invited with a WIP badge.
  const wipSupplierIds = new Set(
    workflows
      .filter(w => WIP_WORKFLOW_STATUSES.includes(w.status))
      .map(w => w.supplier)
  );
  // CR-5953 rework: Invited and Pending Due Diligence must be mutually
  // exclusive — a supplier should never appear in both at the same time
  // (QA: a supplier in the Invited stage was also listed under Pending Due
  // Diligence). The single source of truth for "has progressed to due
  // diligence" is whether the supplier has actually submitted a
  // questionnaire — i.e. they surface in the PDD reviews (FilledDataset) or
  // as a submitted SupplierQualification. Until then they belong in Invited
  // (sent / WIP); once submitted they move to PDD and drop out of Invited.
  // A submitted questionnaire is not the only way in. A supplier can be moved to
  // due diligence and have no FilledDataset rows at all (none assigned yet), and
  // inferring the stage purely from submissions pinned them in Invited forever:
  // the Invited rule kept them because nothing said "due diligence", while PDD's
  // orphan-workflow path refused them precisely because they were still Invited
  // (deriveOrphanWorkflows -> invitedStageSupplierIds). The profile's own
  // onboarding_status is the backend's answer to that question, so trust it too.
  // As at :485, the minimal payload carries company_id, not company.
  const suppliersInDueDiligence = new Set<number>(
    [
      ...pendingReviews.map(r => r.supplier),
      ...submittedQualifications.map(q => q.supplier),
      ...dueDiligenceSuppliers.map(s => s.company_id ?? s.company),
    ].filter((s): s is number => s != null)
  );
  // A supplier can reach approval without ever submitting a questionnaire, so
  // due diligence alone does not cover every way out of the Invited stage. Its
  // workflow row is then left behind on an early status and the supplier shows
  // as WIP under Invited while also sitting in Approved. Approval is the end of
  // the process wherever it came from, so it takes them out of Invited too.
  // The minimal profile payload carries company_id, not company — the type
  // declares both, so reading the wrong one type-checks and then quietly
  // yields undefined for every row.
  const approvedSupplierIds = new Set<number>(
    approvedSuppliers
      .map(s => s.company_id ?? s.company)
      .filter((s): s is number => s != null)
  );
  // Spec asks for "WIP" copy specifically; the invitation row itself
  // still has status='accepted' on the BE, so synthesise a display
  // status purely on the FE.
  // CR-5965: the membership rule now lives in selectInvitedStageInvitations so
  // the Pending Invitations tile applies the identical predicate to the
  // unfiltered list and can never drift from what this tab shows.
  const invitedStageContext = { suppliersInDueDiligence, approvedSupplierIds, wipSupplierIds };
  const invitedTabInvitations = selectInvitedStageInvitations(filteredInvitations, invitedStageContext);
  const filteredDraftSuppliers = draftSuppliers.filter(s =>
    matchesSearch(s.company_name, s.supplier_code)
  );
  const filteredApprovedSuppliers = approvedSuppliers.filter(s =>
    matchesSearch(s.company_name, s.supplier_code)
  );
  const filteredPendingInitial = pendingInitialSuppliers.filter(p =>
    matchesSearch((p as unknown as Record<string, string>)['company_name'])
  );
  const filteredPendingReviews = pendingReviews.filter(fd =>
    matchesSearch(fd.supplier_name, (fd as unknown as Record<string, string>)['dataset_name'])
  );
  // CR-5984 round 3: surface workflows whose customer has not yet assigned
  // any onboarding questionnaires. Earlier rounds cross-referenced the
  // FilledDataset list endpoint, but the two endpoints can be scoped
  // differently for the same user (FilledDatasetViewSet widened via
  // UserSitePermission in round 1; if any future drift reappears the
  // cross-reference quietly mislabels suppliers as orphan). The backend
  // now annotates each workflow with its own FilledDataset count, so
  // orphan detection is a self-contained per-row check: workflow is in
  // a post-acceptance state AND the workflow itself has zero linked
  // FilledDatasets. Suppliers with questionnaires assigned (any status)
  // are excluded from the orphan list and continue to appear via the
  // questionnaire-driven pendingReviews path.
  // CR-5984 Path B: a supplier with SupplierQualifications is also NOT orphan,
  // even when their FilledDataset count is zero. Build a quick lookup of
  // supplier_ids with any active SQ so we can mask them out of the orphan list.
  const suppliersWithQualifications = new Set<number>(submittedQualifications.map(q => q.supplier));
  // CR-5953 rework: anyone still represented in the Invited tab (a supplier
  // who has not yet submitted their questionnaires) must not also surface
  // here as an "orphan" with the misleading "no questionnaires have been
  // assigned" copy — questionnaires assigned at Approve & Send Invitation
  // time are simply outstanding, not missing. This enforces the spec rule
  // that Invited and Pending Due Diligence are mutually exclusive.
  const invitedStageSupplierIds = new Set<number>(
    invitedTabInvitations.map(i => i.supplier).filter((s): s is number => s != null)
  );
  // CR-5953 rework 2: a supplier still in Pending Approvals (submitted via the
  // Add Supplier wizard, awaiting INITIAL approval before any invitation) must
  // never also surface in Pending Due Diligence. QA: a supplier appeared in
  // both "Pending Approvals" and "Pending Due Diligence" with the misleading
  // "accepted the invitation — no questionnaires assigned" copy, even though it
  // had not been invited yet. The prior rework only excluded the Invited stage;
  // exclude the Pending-Approvals stage too. PendingInitialSupplier carries a
  // company_id; pendingApprovals (invitations awaiting approval) carry supplier.
  const pendingApprovalSupplierIds = new Set<number>(
    [
      ...pendingInitialSuppliers.map(p => p.company_id),
      ...pendingApprovals.map(i => i.supplier),
    ].filter((s): s is number => s != null)
  );
  const orphanWorkflows = deriveOrphanWorkflows(workflows, {
    suppliersWithQualifications,
    invitedStageSupplierIds,
    pendingApprovalSupplierIds,
    matchesSearch,
  });
  const filteredSubmittedQualifications = submittedQualifications.filter(q =>
    matchesSearch(q.supplier_name, q.template_name)
  );
  const filteredSettledQualifications = settledQualifications.filter(q =>
    matchesSearch(q.supplier_name, q.template_name)
  );

  // CR-5965: statistic tiles derived from the same records the tabs render,
  // pre-search so they describe the whole pipeline. Previously they came from
  // GET /onboarding/dashboard/, which counts different models with different
  // status sets — QA saw "0 Approved" over a tab listing 13 suppliers.
  const stageCounts = computeOnboardingStageCounts({
    invitations,
    workflows,
    pendingReviews,
    submittedQualifications,
    approvedSuppliers,
    pendingInitialSuppliers,
    pendingApprovals,
    invitedStage: invitedStageContext,
  });

  // Reset paging whenever the search changes
  useEffect(() => { setDraftPage(1); setApprovedPage(1); }, [searchQuery]);

  const paginate = <T,>(items: T[], page: number): T[] =>
    items.slice((page - 1) * TAB_PAGE_SIZE, page * TAB_PAGE_SIZE);
  const pageCount = (total: number) => Math.max(1, Math.ceil(total / TAB_PAGE_SIZE));

  const pagedDraftSuppliers = paginate(filteredDraftSuppliers, draftPage);
  const pagedApprovedSuppliers = paginate(filteredApprovedSuppliers, approvedPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (isSupplier) {
    return (
      <SupplierOnboardingView
        myFilledDatasets={myFilledDatasets}
        myWorkflow={myWorkflow}
        onRefresh={fetchSupplierData}
      />
    );
  }

  return (
    <div className="p-6 space-y-6" data-tour="onboarding-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Supplier Onboarding</h1>
          <p className="text-muted-foreground">Manage supplier invitations, onboarding workflows, and questionnaires</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/suppliers/wizard/new')}><Plus className="h-4 w-4 mr-2" />Add supplier</Button>
          <Button variant="outline" onClick={fetchData}><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium">Pending Invitations</CardTitle><Mail className="h-4 w-4 text-muted-foreground" /></CardHeader>
          <CardContent><div className="text-2xl font-bold">{stageCounts.pendingInvitations}</div><p className="text-xs text-muted-foreground">{stageCounts.totalInvitations} total invitations</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium">Active Onboardings</CardTitle><Clock className="h-4 w-4 text-muted-foreground" /></CardHeader>
          <CardContent><div className="text-2xl font-bold">{stageCounts.activeOnboardings}</div><p className="text-xs text-muted-foreground">Suppliers completing onboarding steps</p></CardContent>
        </Card>
        <Card data-tour="pending-approvals">
          <CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium">Pending Due Diligence</CardTitle><AlertTriangle className="h-4 w-4 text-orange-500" /></CardHeader>
          <CardContent><div className="text-2xl font-bold">{stageCounts.pendingDueDiligence}</div><p className="text-xs text-muted-foreground">{pendingReviews.length + submittedQualifications.length} questionnaires to review</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium">Approved</CardTitle><CheckCircle2 className="h-4 w-4 text-green-500" /></CardHeader>
          <CardContent><div className="text-2xl font-bold">{stageCounts.approved}</div><p className="text-xs text-muted-foreground">Suppliers fully onboarded</p></CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search suppliers..." value={searchQuery} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)} className="pl-9" />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="drafts" className="flex items-center gap-2">
            <CircleDashed className="h-4 w-4" />Drafts
            {draftSuppliers.length > 0 && (<Badge variant="secondary" className="ml-1">{draftSuppliers.length}</Badge>)}
          </TabsTrigger>
          <TabsTrigger value="approvals" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />Pending Approvals
            {(pendingInitialSuppliers.length + pendingApprovals.length) > 0 && (
              <Badge variant="destructive" className="ml-1">{pendingInitialSuppliers.length + pendingApprovals.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="invitations" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />Invited
            {/* CR-5953: badge counts both unopened/in-flight invitations
                AND suppliers who've registered but haven't yet submitted
                their questionnaires (WIP rows). */}
            {invitedTabInvitations.length > 0 && (
              <Badge variant="secondary" className="ml-1">{invitedTabInvitations.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="reviews" className="flex items-center gap-2">
            <Eye className="h-4 w-4" />Pending Due Diligence
            {/* CR-4794: badge counts distinct suppliers awaiting review, not
                questionnaires. A supplier with 3 questionnaires outstanding
                should count as 1 — the tab is a per-supplier worklist.
                CR-5984: also count suppliers whose workflow is post-acceptance
                but has no FilledDataset rows (orphan workflows).
                CR-5953 rework 2: derive the count from the SAME grouping the
                PDD tab renders so the badge can't diverge from the card count
                (QA saw badge "3" while 5 cards were listed). */}
            {(() => {
              const pddCount = groupPendingDueDiligenceSuppliers(
                filteredPendingReviews, filteredSubmittedQualifications, orphanWorkflows,
              ).length;
              return pddCount > 0 ? (
                <Badge variant="destructive" className="ml-1">{pddCount}</Badge>
              ) : null;
            })()}
          </TabsTrigger>
          <TabsTrigger value="declined" className="flex items-center gap-2">
            <ThumbsDown className="h-4 w-4" />Declined
            {/* CR-4837: match the other tabs' count indicator. Source is the
                same union the Declined panel renders (declined workflows +
                declined / cancelled invitations). */}
            {(() => {
              const declinedCount =
                workflows.filter(w => w.status === 'declined').length +
                invitations.filter(i => i.status === 'declined' || i.status === 'cancelled').length;
              return declinedCount > 0 ? (
                <Badge variant="outline" className="ml-1">{declinedCount}</Badge>
              ) : null;
            })()}
          </TabsTrigger>
          <TabsTrigger value="approved" className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />Approved
            {approvedSuppliers.length > 0 && (<Badge variant="outline" className="ml-1">{approvedSuppliers.length}</Badge>)}
          </TabsTrigger>
        </TabsList>

        {/* Drafts Tab — wizard in progress, not yet submitted for customer review */}
        <TabsContent value="drafts" className="mt-4">
          <DraftSuppliersTable
            filteredDraftSuppliers={filteredDraftSuppliers}
            pagedDraftSuppliers={pagedDraftSuppliers}
            searchQuery={searchQuery}
            draftPage={draftPage}
            setDraftPage={setDraftPage}
            pageCount={pageCount}
            pageSize={TAB_PAGE_SIZE}
            onContinue={(companyId) => navigate(`/suppliers/wizard/${companyId}`)}
            onChanged={fetchData}
            canDelete={canApproveSuppliers || !!(user?.is_staff || user?.is_superuser)}
          />
        </TabsContent>

        {/* Pending Approvals Tab - kept inline due to review modal complexity */}
        <TabsContent value="approvals" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Suppliers Awaiting Initial Approval</CardTitle>
              <CardDescription>New suppliers submitted via the Add Supplier wizard. Review and approve to send invitation with questionnaire.</CardDescription>
            </CardHeader>
            <CardContent>
              {filteredPendingInitial.length === 0 ? (
                <div className="text-center py-6">
                  <CheckCircle2 className="h-10 w-10 mx-auto text-green-500 mb-3" />
                  <p className="text-sm text-muted-foreground">{searchQuery ? 'No matches in pending approvals' : 'No suppliers pending initial approval'}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Supplier</TableHead><TableHead>Risk Level</TableHead><TableHead>Est. Annual Spend</TableHead>
                      <TableHead>Submitted By</TableHead><TableHead>Submitted</TableHead><TableHead className="w-[100px]">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPendingInitial.map((supplier) => (
                      <TableRow key={supplier.id}>
                        <TableCell><div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" /><span className="font-medium">{supplier.company_name}</span></div></TableCell>
                        <TableCell>{supplier.risk_level && (<Badge className={supplier.risk_level === 'high' ? 'bg-red-100 text-red-800' : supplier.risk_level === 'medium' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}>{supplier.risk_level.toUpperCase()}</Badge>)}</TableCell>
                        <TableCell>{supplier.estimated_annual_spend ? formatCurrency(Number(supplier.estimated_annual_spend)) : '-'}</TableCell>
                        <TableCell>{supplier.submitted_by || 'Unknown'}</TableCell>
                        <TableCell className="text-muted-foreground">{formatDistanceToNow(new Date(supplier.submitted_at), { addSuffix: true })}</TableCell>
                        <TableCell><Button size="sm" onClick={() => handleOpenReviewModal(supplier.id)}><Eye className="h-4 w-4 mr-1" />Review</Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Review Modal */}
          <Dialog open={reviewModalOpen} onOpenChange={(open) => { if (!open) handleCloseReviewModal(); }}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
              {detailLoading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
              ) : supplierDetail ? (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-3">
                      <Building2 className="h-5 w-5" />{supplierDetail.company_details.name}
                      {(() => {
                        const answers = supplierDetail.risk_answers || [];
                        const scored = answers.filter(a => a.max_score != null && a.max_score > 0);
                        if (scored.length === 0) return supplierDetail.workflow.initial_risk_level ? (
                          <Badge className={supplierDetail.workflow.initial_risk_level === 'high' ? 'bg-red-100 text-red-800' : supplierDetail.workflow.initial_risk_level === 'medium' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}>{supplierDetail.workflow.initial_risk_level.toUpperCase()}</Badge>
                        ) : null;
                        const total = scored.reduce((s, a) => s + (a.score ?? 0), 0);
                        const max = scored.reduce((s, a) => s + (a.max_score ?? 0), 0);
                        const pct = max > 0 ? Math.round((total / max) * 100) : 0;
                        const level = pct >= 66 ? 'HIGH' : pct >= 33 ? 'MEDIUM' : 'LOW';
                        const color = pct >= 66 ? 'bg-red-100 text-red-800' : pct >= 33 ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800';
                        return <Badge className={color}>{level}</Badge>;
                      })()}
                    </DialogTitle>
                    <DialogDescription>Review supplier details and risk assessment before approving or rejecting.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-5 py-2">
                    {/* CR-5391: Show only fields that the onboarding wizard
                        captures, so the popup never advertises data the form
                        never collected (Website / Reg No. / Employee Count
                        were the original offenders). */}
                    <div>
                      <h4 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wider">Company Details</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {([
                          { label: 'Company Name', value: supplierDetail.company_details.name || '-' },
                          { label: 'Email', value: supplierDetail.company_details.primary_email },
                          { label: 'Phone', value: supplierDetail.company_details.primary_phone },
                          { label: 'Address', value: [supplierDetail.company_details.address_line_1, supplierDetail.company_details.city, supplierDetail.company_details.country].filter(Boolean).join(', ') },
                        ] as Array<{ label: string; value: string | null | undefined }>)
                          .filter(item => item.value && item.value !== '-' ? true : item.label === 'Company Name')
                          .map((item, idx) => (
                            <div key={idx} className="bg-muted/30 p-3 rounded-md border">
                              <p className="text-xs text-muted-foreground">{item.label}</p>
                              <p className="text-sm font-medium">{item.value || '-'}</p>
                            </div>
                          ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {supplierDetail.workflow.estimated_annual_spend && (
                        <div className="bg-muted/30 p-3 rounded-md border"><p className="text-xs text-muted-foreground">Est. Annual Spend</p><p className="text-sm font-medium">{formatCurrency(Number(supplierDetail.workflow.estimated_annual_spend))}</p></div>
                      )}
                      {supplierDetail.workflow.submitted_at && (
                        <div className="bg-muted/30 p-3 rounded-md border"><p className="text-xs text-muted-foreground">Submitted At</p><p className="text-sm font-medium">{format(new Date(supplierDetail.workflow.submitted_at), 'MMM d, yyyy HH:mm')}</p></div>
                      )}
                      {supplierDetail.workflow.submitted_by && (
                        <div className="bg-muted/30 p-3 rounded-md border"><p className="text-xs text-muted-foreground">Submitted By</p><p className="text-sm font-medium">{supplierDetail.workflow.submitted_by}</p></div>
                      )}
                    </div>
                    {supplierDetail.risk_answers.length > 0 ? (
                      <div>
                        <h4 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wider">Risk Assessment Answers</h4>
                        <div className="space-y-2">
                          {supplierDetail.risk_answers.map((answer, idx) => (
                            <div key={idx} className="flex items-start gap-3 p-3 bg-muted/30 rounded-md border">
                              <span className="text-xs font-bold text-muted-foreground mt-0.5 shrink-0">{idx + 1}.</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm">{answer.question}</p>
                                <p className="text-sm font-medium mt-1 text-blue-700">{answer.answer || 'No answer'}</p>
                              </div>
                              {answer.max_score != null && (<Badge variant="outline" className="text-xs shrink-0">{answer.score ?? 0} / {answer.max_score}</Badge>)}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-amber-50 border border-amber-200 rounded-md p-3"><p className="text-sm text-amber-800">No risk assessment answers were recorded for this submission.</p></div>
                    )}

                    {/* Spend + Risk Score Summary Badges */}
                    {(() => {
                      const answers = supplierDetail.risk_answers || [];
                      const scoredAnswers = answers.filter(a => a.max_score != null && a.max_score > 0);
                      const totalScore = scoredAnswers.reduce((sum, a) => sum + (a.score ?? 0), 0);
                      const maxPossible = scoredAnswers.reduce((sum, a) => sum + (a.max_score ?? 0), 0);
                      const riskPct = maxPossible > 0 ? Math.round((totalScore / maxPossible) * 100) : 0;
                      const riskLevel = riskPct >= 66 ? 'High' : riskPct >= 33 ? 'Medium' : 'Low';
                      const riskColor = riskPct >= 66 ? 'bg-red-100 text-red-800' : riskPct >= 33 ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800';
                      const spend = supplierDetail.workflow.estimated_annual_spend;

                      return (scoredAnswers.length > 0 || spend) ? (
                        <div className="flex flex-wrap items-center gap-2 pt-2">
                          {spend && (
                            <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 text-sm px-3 py-1">
                              Spend: {formatCurrency(Number(spend))}
                            </Badge>
                          )}
                          {scoredAnswers.length > 0 && (
                            <Badge className={`${riskColor} hover:${riskColor} text-sm px-3 py-1`}>
                              Risk Score: {totalScore}/{maxPossible} ({riskPct}%)
                            </Badge>
                          )}
                          {scoredAnswers.length > 0 && (
                            <Badge className={`${riskColor} hover:${riskColor} text-sm px-3 py-1`}>
                              Risk Level: {riskLevel}
                            </Badge>
                          )}
                        </div>
                      ) : null;
                    })()}
                  </div>
                  {/* Questionnaire Assignment — CR-4141 rework 2: the picker only
                      takes effect when the supplier is approved, so it is dead
                      UI for roles that cannot approve (e.g. Buyer). */}
                  {canApproveSuppliers && availableTemplates.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wider">Assign Questionnaires</h4>
                      <p className="text-xs text-muted-foreground mb-2">Select which questionnaires to assign when approving this supplier.</p>
                      <div className="space-y-2">
                        {availableTemplates.map((t) => (
                          <label key={t.key} className="flex items-center gap-3 p-2 rounded-md border hover:bg-muted/30 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedTemplateIds.includes(t.key)}
                              onChange={(e) => {
                                if (e.target.checked) setSelectedTemplateIds(prev => [...prev, t.key]);
                                else setSelectedTemplateIds(prev => prev.filter(k => k !== t.key));
                              }}
                              className="h-4 w-4 rounded border-gray-300"
                            />
                            <div>
                              <p className="text-sm font-medium">{t.name}</p>
                              <p className="text-xs text-muted-foreground">{t.type} — {t.questions_count} questions</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <DialogFooter className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    {canApproveSuppliers && (
                      <p className="text-xs text-muted-foreground hidden sm:block">
                        {selectedTemplateIds.length > 0
                          ? `Approving will create an invitation and assign ${selectedTemplateIds.length} questionnaire(s).`
                          : 'Select at least one questionnaire — a supplier approved with none cannot be progressed to due diligence.'}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <Button variant="outline" onClick={() => { if (supplierDetail?.id) { setReviewModalOpen(false); navigate(`/suppliers/wizard/${supplierDetail.company_id}?step=1`); } }}>Open supplier wizard</Button>
                      {canApproveSuppliers && (
                        <>
                          <Button variant="destructive" onClick={() => reviewSupplierId && handleRejectPendingSupplier(reviewSupplierId)} disabled={actionLoading === `supplier-${reviewSupplierId}`}><ThumbsDown className="h-4 w-4 mr-2" />Reject</Button>
                          {/* Approving with nothing attached creates a supplier
                              that can never be finalised — questionnaires bind
                              only here. Block it at the button; the backend
                              refuses it too. */}
                          <Button
                            onClick={() => reviewSupplierId && handleApprovePendingSupplier(reviewSupplierId)}
                            disabled={actionLoading === `supplier-${reviewSupplierId}` || selectedTemplateIds.length === 0}
                            title={selectedTemplateIds.length === 0 ? 'Select at least one questionnaire before approving' : undefined}
                          >
                            {actionLoading === `supplier-${reviewSupplierId}` ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ThumbsUp className="h-4 w-4 mr-2" />}Approve & Send Invitation
                          </Button>
                        </>
                      )}
                    </div>
                  </DialogFooter>
                </>
              ) : null}
            </DialogContent>
          </Dialog>

          {/* Invitation Approvals */}
          {pendingApprovals.length > 0 && (
            <Card>
              <CardHeader><CardTitle>Invitations Awaiting Approval</CardTitle><CardDescription>Review and approve invitations before they are sent to suppliers</CardDescription></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow><TableHead>Company</TableHead><TableHead>Contact</TableHead><TableHead>Risk Level</TableHead><TableHead>Est. Spend</TableHead><TableHead>Submitted By</TableHead><TableHead className="w-[150px]">Actions</TableHead></TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingApprovals.map((invitation) => (
                      <TableRow key={invitation.id}>
                        <TableCell><div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" /><span className="font-medium">{invitation.invitee_company_name}</span></div></TableCell>
                        <TableCell><div><div className="font-medium">{invitation.invitee_contact_name}</div><div className="text-sm text-muted-foreground">{invitation.invitee_email}</div></div></TableCell>
                        <TableCell><Badge className={statusColors[invitation.initial_risk_level] || statusColors['pending']}>{invitation.initial_risk_level?.toUpperCase()}</Badge></TableCell>
                        <TableCell>{invitation.estimated_annual_spend ? formatCurrency(Number(invitation.estimated_annual_spend)) : '-'}</TableCell>
                        <TableCell><span className="text-sm">{invitation.submitted_for_approval_by_name || invitation.invited_by_name || '-'}</span></TableCell>
                        <TableCell>
                          {canApproveSuppliers ? (
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => handleApproveToSend(invitation.id)} disabled={actionLoading === invitation.id}>
                                {actionLoading === invitation.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ThumbsUp className="h-4 w-4 mr-1" />Approve</>}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleRejectApproval(invitation.id)} disabled={actionLoading === invitation.id}><ThumbsDown className="h-4 w-4 mr-1" />Reject</Button>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">View only</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="invitations" className="mt-4">
          {/* CR-5953: includes both still-out invitations and accepted+registered
              suppliers whose questionnaires are still outstanding (marked 'wip'). */}
          <InvitationsTab
            filteredInvitations={invitedTabInvitations}
            onSendInvitation={handleSendInvitation}
            onResendInvitation={handleResendInvitation}
            onAdvanceToDueDiligence={handleAdvanceToDueDiligence}
            canApproveSuppliers={canApproveSuppliers}
            advancingInvitationId={advancingInvitationId}
            onActivated={fetchData}
          />
        </TabsContent>

        <TabsContent value="reviews" className="mt-4">
          <PendingReviewsTab
            pendingReviews={filteredPendingReviews}
            orphanWorkflows={orphanWorkflows}
            // CR-6513: every PDD-stage workflow, for counts only. orphanWorkflows
            // has already dropped any supplier holding questionnaires, so it
            // cannot supply the assigned / in-progress totals for exactly the
            // suppliers whose cards need them most.
            countWorkflows={workflows.filter(w => PDD_ORPHAN_WORKFLOW_STATUSES.has(w.status))}
            submittedQualifications={filteredSubmittedQualifications}
            settledQualifications={filteredSettledQualifications}
            onAction={fetchData}
            canApproveSuppliers={canApproveSuppliers}
            /* Only OnboardingDatasets can be attached post-invitation — the
               assign endpoint creates FilledDatasets, which the qualification
               pipeline does not use. */
            assignableDatasets={availableTemplates
              .filter(t => t.source === 'dataset')
              .map(t => ({ id: t.id, name: t.name, question_count: t.questions_count }))}
          />
        </TabsContent>

        <TabsContent value="declined" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Declined Suppliers</CardTitle>
              <CardDescription>Suppliers that were rejected during the onboarding process</CardDescription>
            </CardHeader>
            <CardContent>
              {(() => {
                const declinedWorkflows = workflows.filter(w => w.status === 'declined');
                const declinedInvitations = invitations.filter(i => i.status === 'declined' || i.status === 'cancelled');
                const allDeclined = [
                  ...declinedWorkflows.map(w => ({
                    id: `wf-${w.id}`,
                    name: w.supplier_name || 'Unknown',
                    reason: w.decline_reason || 'No reason provided',
                    date: w.declined_at || w.started_at,
                    type: 'workflow' as const,
                  })),
                  ...declinedInvitations.map(i => ({
                    id: `inv-${i.id}`,
                    name: i.invitee_company_name || 'Unknown',
                    reason: i.rejection_reason || 'No reason provided',
                    date: i.sent_at,
                    type: 'invitation' as const,
                  })),
                ].filter(d => matchesSearch(d.name, d.reason));

                if (allDeclined.length === 0) {
                  return (
                    <div className="text-center py-6">
                      <CheckCircle2 className="h-10 w-10 mx-auto text-green-500 mb-3" />
                      <p className="text-sm text-muted-foreground">No declined suppliers</p>
                    </div>
                  );
                }

                return (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Supplier</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Declined</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allDeclined.map((item) => {
                        const isHighlighted = item.id === highlightedDeclinedId;
                        return (
                        <TableRow
                          key={item.id}
                          ref={isHighlighted ? highlightedDeclinedRowRef : undefined}
                          className={isHighlighted ? 'bg-amber-50 transition-colors' : undefined}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{item.name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="max-w-[300px]">
                            <p className="text-sm text-muted-foreground truncate">{item.reason}</p>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {item.type === 'workflow' ? 'Onboarding' : 'Invitation'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {item.date ? formatDistanceToNow(new Date(item.date), { addSuffix: true }) : '-'}
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Approved Tab — fully onboarded suppliers with audit trail */}
        <TabsContent value="approved" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Approved Suppliers</CardTitle>
              <CardDescription>Suppliers who have completed onboarding. Click to open their profile.</CardDescription>
            </CardHeader>
            <CardContent>
              {filteredApprovedSuppliers.length === 0 ? (
                <div className="text-center py-6">
                  <CheckCircle2 className="h-10 w-10 mx-auto text-green-500 mb-3" />
                  <p className="text-sm text-muted-foreground">{searchQuery ? 'No approved suppliers match your search' : 'No approved suppliers'}</p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Supplier</TableHead>
                        <TableHead>Approved By</TableHead>
                        <TableHead>Approved On</TableHead>
                        <TableHead className="w-[120px]">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pagedApprovedSuppliers.map((sp) => (
                        <TableRow key={sp.id}>
                          <TableCell><div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" /><span className="font-medium">{sp.company_name || `Supplier #${sp.id}`}</span></div></TableCell>
                          <TableCell className="text-muted-foreground">{sp.approved_by || '—'}</TableCell>
                          <TableCell className="text-muted-foreground">{sp.approved_at ? format(new Date(sp.approved_at), 'dd MMM yyyy') : '—'}</TableCell>
                          <TableCell><Button size="sm" variant="outline" onClick={() => navigate(`/suppliers/${sp.company_id ?? sp.company}`)}><Eye className="h-4 w-4 mr-1" />View</Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {pageCount(filteredApprovedSuppliers.length) > 1 && (
                    <div className="flex items-center justify-between pt-4 text-sm">
                      <span className="text-muted-foreground">Showing {((approvedPage - 1) * TAB_PAGE_SIZE) + 1}–{Math.min(approvedPage * TAB_PAGE_SIZE, filteredApprovedSuppliers.length)} of {filteredApprovedSuppliers.length}</span>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" disabled={approvedPage === 1} onClick={() => setApprovedPage(p => Math.max(1, p - 1))}>Previous</Button>
                        <Button size="sm" variant="outline" disabled={approvedPage >= pageCount(filteredApprovedSuppliers.length)} onClick={() => setApprovedPage(p => Math.min(pageCount(filteredApprovedSuppliers.length), p + 1))}>Next</Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Reject Reason Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={(open) => { if (!open) { setRejectDialogOpen(false); setRejectTarget(null); setRejectReason(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <ThumbsDown className="h-5 w-5" />
              Reject Supplier
            </DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this supplier. This will be recorded in the audit trail.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason for rejection..."
              rows={4}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
              autoFocus
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setRejectDialogOpen(false); setRejectTarget(null); setRejectReason(''); }}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmReject} disabled={!rejectReason.trim()}>
              <ThumbsDown className="h-4 w-4 mr-2" />
              Confirm Rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
